/**
 * useSubscription Hook
 * Source of truth: Supabase `subscriptions` table (via RPC `get_user_subscription_status`).
 * RevenueCat handles regular purchase / restore; referred purchases use Stripe Checkout.
 *
 * SECURITY: We do NOT trust localStorage as authority. The DB RPC is queried on every
 * mount, on auth change, and on window focus. RLS policies on premium content also
 * verify subscription server-side, so even if a client lies, no data leaks.
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import type { CustomerInfo, Package } from '@revenuecat/purchases-js';
import { useAuth } from './useAuth';
import {
    initializeRevenueCat,
    resetRevenueCat,
    ENTITLEMENT_ID,
    isRevenueCatInitialized,
    getRevenueCat
} from '@/lib/revenuecat';
import { trackSubscriptionStarted } from '@/lib/posthog';
import { supabase } from '@/integrations/supabase/client';
import {
    fetchReferralCheckoutBenefit,
    REFERRAL_CLAIMED_EVENT,
    type ReferralCheckoutBenefit,
} from '@/lib/affiliates';

interface SubscriptionContextType {
    // State
    isLoading: boolean;
    isSubscribed: boolean;
    isAlumni: boolean;
    customerInfo: CustomerInfo | null;
    offerings: Package[];
    error: string | null;
    isAdmin: boolean;
    referralBenefit: ReferralCheckoutBenefit | null;

    // Actions
    purchase: (packageToPurchase: Package) => Promise<boolean>;
    restorePurchases: () => Promise<void>;
    refreshCustomerInfo: () => Promise<void>;

    // Metadata
    willRenew: boolean;
    expirationDate: Date | string | null;
    isCancelled: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface DbSubscriptionStatus {
    is_subscribed: boolean;
    is_alumni: boolean;
    is_admin: boolean;
    plan: string | null;
    status: string | null;
    expires_at: string | null;
    will_renew: boolean;
    cancel_at: string | null;
}

function affiliatePlanForPackage(packageToPurchase: Package): 'monthly' | 'yearly' | null {
    const descriptor = [
        packageToPurchase.identifier,
        packageToPurchase.rcBillingProduct?.normalPeriodDuration,
    ].filter(Boolean).join(' ').toLowerCase();

    if (/(annual|year|yearly|anual|p1y)/.test(descriptor)) return 'yearly';
    if (/(month|monthly|mensual|p1m)/.test(descriptor)) return 'monthly';
    return null;
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();

    // Source of truth = DB. Start in loading until we get a verdict from the server.
    const [isLoading, setIsLoading] = useState(true);
    const [dbStatus, setDbStatus] = useState<DbSubscriptionStatus | null>(null);
    const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
    const [offerings, setOfferings] = useState<Package[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [referralBenefit, setReferralBenefit] = useState<ReferralCheckoutBenefit | null>(null);


    const isSubscribed = !!dbStatus?.is_subscribed || !!customerInfo?.entitlements.active[ENTITLEMENT_ID]?.isActive;
    const isAlumni = !!dbStatus?.is_alumni;
    const isAdmin = !!dbStatus?.is_admin;

    const lastFetchRef = useRef<number>(0);

    // Fetch authoritative subscription status from Supabase
    const fetchDbStatus = useCallback(async (): Promise<DbSubscriptionStatus | null> => {
        if (!user) {
            setDbStatus(null);
            return null;
        }

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error: rpcError } = await (supabase as any).rpc('get_user_subscription_status');

            if (rpcError) {
                console.error('[Subscription] DB status error:', rpcError);
                return null;
            }

            const row: DbSubscriptionStatus | undefined = Array.isArray(data) ? data[0] : data;
            if (!row) return null;

            setDbStatus(row);
            lastFetchRef.current = Date.now();
            return row;
        } catch (err) {
            console.error('[Subscription] DB status threw:', err);
            return null;
        }
    }, [user?.id]);

    const fetchCheckoutBenefit = useCallback(async () => {
        if (!user) {
            setReferralBenefit(null);
            return null;
        }
        try {
            const benefit = await fetchReferralCheckoutBenefit();
            setReferralBenefit(benefit);
            return benefit;
        } catch (err) {
            console.warn('[Subscription] Referral benefit lookup failed:', err);
            setReferralBenefit(null);
            return null;
        }
    }, [user?.id]);

    // Sync local RC state from CustomerInfo (for purchase metadata only — not access)
    const syncFromCustomerInfo = useCallback((info: CustomerInfo) => {
        setCustomerInfo(info);
    }, []);

    // Refresh both DB (authority) and RevenueCat (metadata)
    const refreshCustomerInfo = useCallback(async () => {
        await fetchDbStatus();

        if (isRevenueCatInitialized()) {
            try {
                const purchases = getRevenueCat();
                const info = await purchases.getCustomerInfo();
                syncFromCustomerInfo(info);
            } catch (err) {
                console.error('[Subscription] RC refresh error:', err);
            }
        }
    }, [fetchDbStatus, syncFromCustomerInfo]);

    // Initialize on user change
    useEffect(() => {
        let cancelled = false;

        async function initialize() {
            if (!user) {
                resetRevenueCat();
                setDbStatus(null);
                setCustomerInfo(null);
                setOfferings([]);
                setReferralBenefit(null);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            // 1. DB is the source of truth — fetch first.
            await Promise.all([fetchDbStatus(), fetchCheckoutBenefit()]);
            if (cancelled) return;

            // 2. Initialize RevenueCat for purchase flow (best-effort).
            try {
                const purchases = await initializeRevenueCat(user.id);
                if (cancelled) return;

                try {
                    const info = await purchases.getCustomerInfo();
                    if (!cancelled) syncFromCustomerInfo(info);
                } catch (rcErr) {
                    console.warn('[RevenueCat] getCustomerInfo failed:', rcErr);
                }

                try {
                    const offeringsResponse = await purchases.getOfferings();
                    if (!cancelled && offeringsResponse.current) {
                        setOfferings(offeringsResponse.current.availablePackages);
                    }
                } catch (offeringsError) {
                    console.warn('[RevenueCat] Could not fetch offerings:', offeringsError);
                }
            } catch (err) {
                // RC init failure does not block access — DB already decided.
                console.warn('[Subscription] RC init failed (DB still authoritative):', err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }

        initialize();
        return () => { cancelled = true; };
    }, [user?.id, fetchDbStatus, fetchCheckoutBenefit, syncFromCustomerInfo]);

    // Purchase a package
    const purchase = useCallback(async (packageToPurchase: Package): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        let currentReferralBenefit: ReferralCheckoutBenefit | null;
        try {
            currentReferralBenefit = await fetchReferralCheckoutBenefit();
            setReferralBenefit(currentReferralBenefit);
        } catch (benefitError) {
            console.error('[Subscription] Could not verify referral benefit:', benefitError);
            setError('No pudimos verificar tu beneficio antes del pago. Inténtalo de nuevo.');
            setIsLoading(false);
            return false;
        }

        if (currentReferralBenefit?.eligible) {
            const plan = affiliatePlanForPackage(packageToPurchase);
            if (!plan) {
                setError('No pudimos identificar este plan para aplicar tu beneficio.');
                setIsLoading(false);
                return false;
            }

            try {
                const { data, error: checkoutError } = await supabase.functions.invoke(
                    'create-affiliate-checkout',
                    { body: { plan } },
                );
                if (checkoutError) throw checkoutError;
                const checkoutUrl = data?.url;
                if (typeof checkoutUrl !== 'string' || !checkoutUrl.startsWith('https://')) {
                    throw new Error('Invalid checkout URL');
                }
                window.location.assign(checkoutUrl);
                return false;
            } catch (checkoutError) {
                console.error('[Subscription] Affiliate checkout error:', checkoutError);
                setError('No pudimos abrir el pago con tu descuento. Inténtalo de nuevo.');
                return false;
            } finally {
                setIsLoading(false);
            }
        }

        if (!isRevenueCatInitialized()) {
            setError('Subscription system not initialized');
            setIsLoading(false);
            return false;
        }

        try {
            const purchases = getRevenueCat();
            const { customerInfo: newInfo } = await purchases.purchase({ rcPackage: packageToPurchase });
            syncFromCustomerInfo(newInfo);

            // Wait briefly for the RevenueCat webhook to update our DB, then re-fetch authority.
            // We retry up to 5 times because webhook delivery can take a moment.
            let granted = false;
            const ent = newInfo.entitlements.active[ENTITLEMENT_ID];
            if (ent && ent.isActive) {
                granted = true;
            }

            if (!granted) {
                for (let attempt = 0; attempt < 5; attempt++) {
                    await new Promise((r) => setTimeout(r, attempt === 0 ? 800 : 1500));
                    const latestRow = await fetchDbStatus();
                    if (latestRow?.is_subscribed) {
                        granted = true;
                        break;
                    }
                }
            } else {
                setTimeout(() => fetchDbStatus(), 2000);
            }

            if (granted) {
                const product = packageToPurchase.rcBillingProduct;
                trackSubscriptionStarted({
                    plan: packageToPurchase.identifier,
                    price: product?.currentPrice?.amountMicros ? product.currentPrice.amountMicros / 1_000_000 : undefined,
                    currency: product?.currentPrice?.currency,
                    period: product?.normalPeriodDuration ?? undefined,
                });
            }

            return granted;
        } catch (err) {
            if (err && typeof err === 'object' && 'errorCode' in err) {
                const purchaseErr = err as { errorCode: string | number; message?: string };
                if (String(purchaseErr.errorCode).includes('UserCancelled') || String(purchaseErr.errorCode).includes('Cancelled')) {
                    return false;
                }
                setError(purchaseErr.message || 'Purchase failed. Please try again.');
            } else {
                setError('Purchase failed. Please try again.');
            }
            console.error('[Subscription] Purchase error:', err);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [fetchDbStatus, syncFromCustomerInfo]);

    useEffect(() => {
        if (!user) return;
        const refreshBenefit = () => { fetchCheckoutBenefit(); };
        window.addEventListener(REFERRAL_CLAIMED_EVENT, refreshBenefit);
        return () => window.removeEventListener(REFERRAL_CLAIMED_EVENT, refreshBenefit);
    }, [user?.id, fetchCheckoutBenefit]);

    // Restore purchases — re-syncs RC then re-reads DB.
    const restorePurchases = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            if (isRevenueCatInitialized()) {
                const purchases = getRevenueCat();
                const info = await purchases.getCustomerInfo();
                syncFromCustomerInfo(info);
            }
            await fetchDbStatus();
        } catch (err) {
            setError('Failed to restore purchases');
            console.error('[Subscription] Restore error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [fetchDbStatus, syncFromCustomerInfo]);

    // Window focus listener — re-validate from DB (rate-limited to once per 30s)
    useEffect(() => {
        const handleFocus = () => {
            if (!user) return;
            const now = Date.now();
            if (now - lastFetchRef.current < 30_000) return;
            fetchDbStatus();
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [user?.id, fetchDbStatus]);

    // Realtime: react to subscription changes pushed by the webhook
    useEffect(() => {
        if (!user) return;
        const channel = supabase
            .channel(`sub-${user.id}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'subscriptions', filter: `user_id=eq.${user.id}` },
                () => { fetchDbStatus(); }
            )
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [user?.id, fetchDbStatus]);

    const willRenew = dbStatus?.will_renew ?? true;
    const expirationDate = dbStatus?.expires_at ?? null;
    const isCancelled = !!dbStatus?.cancel_at;

    return (
        <SubscriptionContext.Provider
            value={{
                isLoading,
                isSubscribed,
                isAlumni,
                customerInfo,
                offerings,
                error,
                referralBenefit,
                purchase,
                restorePurchases,
                refreshCustomerInfo,
                willRenew,
                expirationDate,
                isCancelled,
                isAdmin,
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
}

export function useSubscription() {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
}
